import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { GENDER } from '@/features/account-requests/types';
import { LANGUAGE } from '@/features/auth/register.api';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { subjectColor, useSubjectLabel } from './subject-labels';
import { SubjectDot } from './subjects';
import {
  useUsageBreakdown,
  type BreakdownDimension,
  type SegmentDto,
  type Subject,
  type UsageFilters,
} from './usage-analytics.api';
import { ratio, useDomainLabel } from './usage-format';
import { csvShare, downloadCsv } from './usage-csv';
import { Empty, ErrorNotice, ExportButton, ShareCell } from './usage-parts';

/**
 * One subject's visits, split along a dimension.
 *
 * The same table as the top pages, with slices for rows: a column per subject
 * and each cell that slice's share of the subject's own visits. It answers
 * "who is behind this group's activity" without building a subject for every
 * age band by hand.
 */

/** Youngest first. An age scale read in order of size is a scale scrambled. */
const AGE_ORDER = [
  'under-18',
  '18-24',
  '25-34',
  '35-44',
  '45-54',
  '55-64',
  '65+',
  'unknown',
];

function useSegmentLabel(dimension: BreakdownDimension) {
  const { t } = useTranslation();
  const domainLabel = useDomainLabel();

  return (segment: { value: string; name: string | null }) => {
    switch (dimension) {
      case 'gender':
        if (segment.value === String(GENDER.male)) return t('usage.male');
        if (segment.value === String(GENDER.female)) return t('usage.female');
        return t('usage.genderNotSaid');
      case 'language':
        if (segment.value === String(LANGUAGE.en)) return t('common.english');
        if (segment.value === String(LANGUAGE.es)) return t('common.spanish');
        return t('usage.otherLanguage');
      case 'age':
        if (segment.value === 'unknown') return t('usage.ageUnknown');
        if (segment.value === 'under-18') return t('usage.ageUnder18');
        return segment.value.replace('-', '–');
      case 'domain':
        return domainLabel(segment.value);
      case 'business':
        return segment.value === 'global'
          ? t('pageHistory.global')
          : (segment.name ?? segment.value);
    }
  };
}

export function UsageBreakdown({
  filters,
  subjects,
  dimension,
}: {
  filters: UsageFilters;
  subjects: Subject[];
  dimension: BreakdownDimension;
}) {
  const { t } = useTranslation();
  const label = useSubjectLabel();
  const segmentLabel = useSegmentLabel(dimension);
  const breakdown = useUsageBreakdown(filters, subjects, dimension);

  const { rows, totals, scale } = useMemo(() => {
    const bySubject = new Map(
      (breakdown.data ?? []).map((entry) => [entry.key, entry.segments])
    );

    // Every visit falls in exactly one slice, so the slices sum to the
    // subject's visits and need no second request for a denominator.
    const sums = new Map<string, number>();
    const merged = new Map<
      string,
      { value: string; name: string | null; cells: Map<string, SegmentDto>; peak: number }
    >();

    for (const subject of subjects) {
      const segments = bySubject.get(subject.key) ?? [];
      const total = segments.reduce((sum, segment) => sum + segment.visits, 0);
      sums.set(subject.key, total);

      for (const segment of segments) {
        const row = merged.get(segment.value) ?? {
          value: segment.value,
          name: segment.name,
          cells: new Map<string, SegmentDto>(),
          peak: 0,
        };
        row.cells.set(subject.key, segment);
        row.peak = Math.max(row.peak, ratio(segment.visits, total));
        merged.set(segment.value, row);
      }
    }

    const list = [...merged.values()];
    if (dimension === 'age') {
      list.sort((a, b) => AGE_ORDER.indexOf(a.value) - AGE_ORDER.indexOf(b.value));
    } else {
      list.sort((a, b) => b.peak - a.peak);
    }

    return {
      rows: list,
      totals: sums,
      scale: Math.max(0, ...list.map((row) => row.peak)),
    };
  }, [breakdown.data, subjects, dimension]);

  if (breakdown.isError) {
    return (
      <ErrorNotice
        message={breakdown.error?.message || t('usage.loadFailed')}
        onRetry={() => void breakdown.refetch()}
      />
    );
  }
  if (breakdown.isLoading) return <Skeleton className="h-56 rounded-xl" />;
  if (rows.length === 0) return <Empty>{t('usage.noVisits')}</Empty>;

  return (
    <div className="relative rounded-xl">
      <BusyOverlay show={breakdown.isFetching} />
      <div
        className={cn(
          'bg-card overflow-x-auto rounded-xl border',
          breakdown.isFetching && 'opacity-60 transition-opacity'
        )}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t(`usage.dimensions.${dimension}`)}</TableHead>
              {subjects.map((subject) => (
                <TableHead key={subject.key} className="min-w-36">
                  <span className="flex items-center gap-2">
                    <SubjectDot subject={subject} />
                    <span className="truncate">{label(subject)}</span>
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.value}>
                <TableCell className="font-medium">{segmentLabel(row)}</TableCell>
                {subjects.map((subject) => {
                  const cell = row.cells.get(subject.key);
                  return (
                    <TableCell
                      key={subject.key}
                      title={
                        cell
                          ? t('usage.pageCell', {
                              visits: formatNumber(cell.visits),
                              users: formatNumber(cell.activeUsers),
                            })
                          : undefined
                      }
                    >
                      {cell ? (
                        <ShareCell
                          share={ratio(cell.visits, totals.get(subject.key) ?? 0)}
                          scale={scale}
                          color={subjectColor(subject)}
                        />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="mt-2">
        <ExportButton
          onExport={() =>
            downloadCsv(`usage_by_${dimension}.csv`, [
              [
                t(`usage.dimensions.${dimension}`),
                t('usage.subject'),
                t('usage.visits'),
                t('usage.activeUsers'),
                t('usage.share'),
              ],
              ...rows.flatMap((row) =>
                subjects.flatMap((subject) => {
                  const cell = row.cells.get(subject.key);
                  if (!cell) return [];
                  return [
                    [
                      segmentLabel(row),
                      label(subject),
                      cell.visits,
                      cell.activeUsers,
                      csvShare(ratio(cell.visits, totals.get(subject.key) ?? 0)),
                    ],
                  ];
                })
              ),
            ])
          }
        />
      </div>
    </div>
  );
}
